import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { parseAsInteger, useQueryState } from 'nuqs';
import { useUpdateScheduleEntry, useUpsertBreak } from '../../../entities/schedule';
import { UpsertAppointmentModal, useUpsertAppointment } from '../../../features/appointment-upsert';
import {
  PlanningBar,
  applyDraftDrag,
  draftLayout,
  draftToDefaultSlot,
  slotToDraft,
  useDispositionMode,
  usePlanningDraft,
  useRescheduleSource,
} from '../../../features/disposition-planning';
import { UpsertTaskModal, useUpsertTask } from '../../../features/task-upsert';
import { useAuthStore } from '../../../features/auth';
import { getBlockColors } from '../model/blockColors';
import {
  BOARD_WIDTH,
  TECHNICIAN_COL_WIDTH,
  detectDragCollision,
  isOutsideShift,
  sameSlot,
  slotDayIso,
  type ScheduleOverrides,
  type ScheduleSlot,
  type SegmentHandle,
} from '../../../shared/lib/schedule';
import { routes } from '../../../shared/config/routes';
import { useScheduleConflictFlow } from '../../../shared/hooks/useScheduleConflictFlow';
import { ScheduleConflictDialogs } from '../../../shared/ui/feedback/ScheduleConflictDialogs/ScheduleConflictDialogs';
import { BOARD_LAYER } from '../model/boardLayers';
import type { DragPreview, PlanningDraft } from '../model/boardPreview';
import { buildSchedulePatch, type DragHandle, type DragOutcome } from '../model/buildSchedulePatch';
import { DRAFT_KEY, blockSubject, draftSubject, type DragSubject } from '../model/dragSubject';
import { resolveDragPolicy, type DragContext } from '../model/dragPolicy';
import { buildPreviewLayout } from '../model/previewLayout';
import { ROW_TECHNICIAN_ATTR, useBlockDrag } from '../model/useBlockDrag';
import { useDispositionBoard } from '../model/useDispositionBoard';
import type { BoardBlock } from '../model/boardTypes';
import type { DispositionDay } from '../model/useDispositionDay';
import { FOCUS_TECHNICIAN_PARAM } from '../model/useDispositionWeek';
import { BoardCurrentTimeLine } from './BoardCurrentTimeLine';
import { BoardMessage } from './BoardMessage';
import { BoardRow } from './BoardRow';
import { BoardSkeleton } from './BoardSkeleton';
import { BoardTimeAxis } from './BoardTimeAxis';

interface Props {
  /**
   * From `useDispositionDay()`. The page owns the hook so it can render `DispositionDayNav` into
   * `PageLayout`'s `headerChildren`; the widget only reads the day off it.
   */
  day: DispositionDay;
}

/**
 * Width of the docked upsert form. Must match `.sidePanel` in `shared/ui/overlay/Modal.module.css` —
 * the board widens itself by this much while a form is open so the last hours of the day can still
 * be scrolled out from under it, and the scroll-into-view below treats it as the right-hand edge of
 * what the dispatcher can actually see.
 */
const DOCKED_FORM_WIDTH = 560;

/**
 * The disposition board: one row per technician, time running horizontally.
 *
 * Click a block to edit it, drag it to move or resize. All of that runs through one drag primitive
 * with per-kind policies (`useBlockDrag` + `dragPolicy`), rather than the three parallel drag
 * implementations the deleted timeline had.
 */
export const DispositionBoardWidget = ({ day }: Props) => {
  // Read before the board query: the entry being moved is dropped from the rows, so the draft that
  // stands in its place has the slot to itself.
  const plan = useDispositionMode();
  const source = useRescheduleSource(plan);

  const { rows, isLoading, isError, refetch } = useDispositionBoard(
    day.dayIso,
    day.from,
    day.to,
    source && { kind: source.kind, ownerId: source.ownerId },
  );

  const navigate = useNavigate();

  const user = useAuthStore((state) => state.user);
  const dragContext = useMemo<DragContext>(() => ({ userRoles: user?.roles ?? [] }), [user?.roles]);

  const updateEntry = useUpdateScheduleEntry();
  const upsertBreak = useUpsertBreak();

  // The same three-dialog flow the upsert modals use. Until now the drag path had none of it:
  // a rejected drop just snapped back with no explanation (widget README §6).
  const conflictFlow = useScheduleConflictFlow();

  // Planning state first: the drag commit below has to reach `planning.applyDrag` for the draft.
  const planning = usePlanningDraft(day.dayIso, plan.mode, source?.seed);

  const appointmentModal = useUpsertAppointment();
  const taskModal = useUpsertTask();
  const { openCreate: openCreateAppointment, openEdit: openEditAppointment } = appointmentModal;
  const { openCreate: openCreateTask, openEdit: openEditTask } = taskModal;

  /** A form is docked beside the board. It, not the board, is what is being edited. */
  const modalOpen = appointmentModal.isOpen || taskModal.isOpen;

  // ── Live slot ────────────────────────────────────────────────────────────────────────────────

  /**
   * What the open form currently says about *when* — the **single owner of the block being edited**
   * while a form is open, for both directions at once. The modal reports every keystroke into it,
   * and a drag on the board writes back into it instead of sending a request; the calendar draws it
   * and the modal reads it back. Both writes compare by value, so the value handed back to the form
   * it came from stops at the first comparison rather than looping.
   *
   * Dropped when the form closes, which is what makes closing without saving a discard: the board
   * falls back to what it drew before — the planning draft, or the entry the server has.
   */
  const [formSlot, setFormSlot] = useState<ScheduleSlot | null>(null);

  /**
   * Whether the open form says Ganztägig. Kept apart from `formSlot` on purpose — see
   * `UpsertAppointmentModal`'s `onFullDayChange`: anything inside `ScheduleSlot` is compared by
   * `sameSlot` and can therefore make the board write back over the form.
   */
  const [formIsFullDay, setFormIsFullDay] = useState(false);

  /**
   * `null` is never written from here. Both modals stay mounted and the closed one reports `null`
   * continuously, which would otherwise wipe the open one's slot; clearing is the effect below's
   * job, keyed on the thing that actually decides it.
   */
  const handleSlotChange = useCallback((next: ScheduleSlot | null) => {
    if (!next) return;
    setFormSlot((current) => (sameSlot(current, next) ? current : next));
  }, []);

  useEffect(() => {
    if (!modalOpen) {
      setFormSlot(null);
      setFormIsFullDay(false);
    }
  }, [modalOpen]);

  /**
   * The board follows the form's day. A Termin typed onto Thursday is drawn on Thursday rather than
   * silently dropped, and the form keeps everything that was typed — it is never reset by this.
   *
   * `useDispositionDay` rebuilds its callbacks every render, so `goToDay` is read through a ref: in
   * the dependency array it would re-run this on every render of the board.
   */
  const goToDayRef = useRef(day.goToDay);
  useEffect(() => {
    goToDayRef.current = day.goToDay;
  });

  useEffect(() => {
    const target = formSlot && slotDayIso(formSlot);
    if (target && target !== day.dayIso) goToDayRef.current(target);
  }, [formSlot, day.dayIso]);

  /**
   * The form's slot as a block on this board. `null` while a field is half-typed, or while the slot
   * belongs to another day — the effect above is on its way there.
   */
  const liveDraft = useMemo(
    () => (formSlot ? slotToDraft(formSlot, day.dayIso) : null),
    [formSlot, day.dayIso],
  );

  /**
   * The existing entry the open form belongs to, when there is one.
   *
   * It keeps its place in the lane packing — dropping it would make the whole row jump the moment a
   * form opened — and is dimmed instead, with the live block drawn over it. The collision check
   * skips its key, so the block cannot report a conflict with itself.
   */
  const editedBlock = useMemo(() => {
    const appointmentId = appointmentModal.isOpen ? appointmentModal.appointmentId : null;
    const taskId = taskModal.isOpen ? taskModal.taskId : null;
    if (appointmentId == null && taskId == null) return null;

    return (
      rows
        .flatMap((row) => row.blocks)
        .find((block) =>
          appointmentId != null
            ? block.entry.appointmentId === appointmentId
            : block.entry.taskId === taskId,
        ) ?? null
    );
  }, [
    rows,
    appointmentModal.isOpen,
    appointmentModal.appointmentId,
    taskModal.isOpen,
    taskModal.taskId,
  ]);

  /**
   * Colours for the live block: the edited entry's own, so a started Termin keeps looking started,
   * and otherwise the mode's — a block being created has no status yet.
   */
  const liveColors = useMemo(() => {
    if (editedBlock) return getBlockColors(editedBlock.kind, editedBlock.status);
    const kind = taskModal.isOpen || plan.mode === 'schedule-task' ? 'TASK' : 'APPOINTMENT';
    return getBlockColors(kind, null);
  }, [editedBlock, taskModal.isOpen, plan.mode]);

  /**
   * What the live block is allowed to collide with.
   *
   * A Ganztägig Termin spans the technician's whole shift, so it always crosses the Pause — and the
   * API does not call that a conflict: `ScheduleConflictChecker.ignoreBreaks` drops projected breaks
   * from the overlap query whenever `allDay` is set. Without the same skip here the block would sit
   * permanently red and `PlanningBar` would announce an Überschneidung that can never be raised.
   *
   * The same relationship as `isOutsideShift` and `findWorkingHoursViolations`: an advisory that
   * mirrors a server rule, and has to mirror the exceptions too.
   */
  const draftCollisionBlocks = useCallback(
    (blocks: BoardBlock[]) =>
      formIsFullDay ? blocks.filter((block) => block.kind !== 'BREAK') : blocks,
    [formIsFullDay],
  );

  /**
   * Sends one finished drag, and re-sends it with an override when the user confirms a 409.
   *
   * `block` and `outcome` are parameters rather than reads off `drag`, because the retry runs from a
   * dialog button — the pointer gesture is long over by then and `drag` is back to `null`.
   *
   * `mutateAsync`, not `mutate`: neither mutation has an `onError` that could route the rejection
   * out (the update hook is deliberately non-optimistic, so there is nothing to roll back), and
   * `mutate` would swallow it into `isError` where nothing reads it. That silence is what made an
   * off-shift drop look like it simply did not take.
   */
  const commitDrag = useCallback(
    async (block: BoardBlock, outcome: DragOutcome, overrides: ScheduleOverrides = {}) => {
      const patch = buildSchedulePatch(
        block,
        resolveDragPolicy(block, dragContext),
        outcome,
        overrides,
      );
      // A no-op drag — same slot, same technician. Nothing to send.
      if (!patch) return;

      try {
        if (patch.kind === 'break') await upsertBreak.mutateAsync(patch.dto);
        else await updateEntry.mutateAsync({ entryId: patch.entryId, dto: patch.dto });
      } catch (error) {
        // Pass back the overrides this attempt actually used: the API checks working hours before
        // overlaps, so one drop can 409 twice and the second confirmation must keep the first flag.
        conflictFlow.handle(error, {
          overrides,
          attempt: (next) => commitDrag(block, outcome, next),
        });
      }
    },
    [conflictFlow, dragContext, updateEntry, upsertBreak],
  );

  const handleCommit = useCallback(
    (subject: DragSubject, outcome: DragOutcome) => {
      // The draft is not on the server — a finished gesture just moves the local state.
      if (subject.type === 'draft') {
        const handle = outcome.handle as 'move' | SegmentHandle;

        // A form owns the block: the gesture retypes its four scheduling fields and nothing is sent.
        // The modal's submit stays the only thing that writes — dragging must not save behind a
        // half-filled form's back.
        if (liveDraft) {
          const next = applyDraftDrag(liveDraft, handle, outcome.deltaMinutes, outcome.technicianId);
          handleSlotChange(draftToDefaultSlot(next, day.dayIso));
          return;
        }

        planning.applyDrag(handle, outcome.deltaMinutes, outcome.technicianId);
        return;
      }

      void commitDrag(subject.block, outcome);
    },
    [commitDrag, planning, liveDraft, handleSlotChange, day.dayIso],
  );

  const { drag, startDrag, wasDragged } = useBlockDrag(handleCommit);

  const onDragStart = useCallback(
    (block: BoardBlock, handle: DragHandle, e: React.PointerEvent) => {
      const policy = resolveDragPolicy(block, dragContext);
      if (handle === 'move' ? !policy.canMove : !policy.handles.includes(handle)) return;
      startDrag(blockSubject(block), handle, policy.canReassign, block.entry.technicianId, e);
    },
    [dragContext, startDrag],
  );

  /** The draft's implicit policy: freely movable, reassignable, all three segment handles. */
  const onDraftDragStart = useCallback(
    (handle: 'move' | SegmentHandle, e: React.PointerEvent) => {
      const placed = liveDraft ?? planning.draft;
      if (!placed) return;
      startDrag(draftSubject(placed), handle, true, placed.technicianId, e);
    },
    [liveDraft, planning.draft, startDrag],
  );

  const preview = useMemo<DragPreview | null>(() => {
    if (!drag) return null;

    // The draft mid-drag: same ghost, same collision check, layout from the same one drag rule.
    if (drag.blockKey === DRAFT_KEY) {
      const placed = liveDraft ?? planning.draft;
      if (!placed) return null;

      const handle = drag.handle as 'move' | SegmentHandle;
      const next = applyDraftDrag(placed, handle, drag.deltaMinutes, drag.technicianId);
      const layout = draftLayout(next, liveColors, handle);
      const targetRow = rows.find((row) => row.technicianId === drag.technicianId);

      return {
        blockKey: DRAFT_KEY,
        technicianId: drag.technicianId,
        layout,
        lane: 0,
        // The entry the form is editing is dimmed and stood in for — it must not flag its own slot.
        colliding: targetRow
          ? detectDragCollision(
              draftCollisionBlocks(targetRow.blocks),
              layout,
              editedBlock?.key ?? DRAFT_KEY,
            ).length > 0
          : false,
        outsideShift: targetRow ? isOutsideShift(layout, targetRow.shift) : false,
      };
    }

    const block = rows.flatMap((row) => row.blocks).find((b) => b.key === drag.blockKey);
    if (!block) return null;

    const layout = buildPreviewLayout(block, day.dayIso, drag.handle, drag.deltaMinutes);
    const targetRow = rows.find((row) => row.technicianId === drag.technicianId);
    // `PreviewLayout` is structurally a `MinuteRange`, so the collision check needs no adapter.
    const colliding = targetRow
      ? detectDragCollision(targetRow.blocks, layout, block.key).length > 0
      : false;

    return {
      blockKey: block.key,
      technicianId: drag.technicianId,
      layout,
      // The block's own lane is meaningless in another row; drop it on the first one.
      lane: drag.technicianId === drag.originTechnicianId ? block.lane : 0,
      colliding,
      // The shift of the row the pointer is over, not the block's own — moving a Termin to a
      // technician who is off at that hour is exactly the case this warns about.
      outsideShift: targetRow ? isOutsideShift(layout, targetRow.shift) : false,
    };
  }, [
    drag,
    rows,
    day.dayIso,
    liveDraft,
    planning.draft,
    liveColors,
    editedBlock,
    draftCollisionBlocks,
  ]);

  // ── Planning mode ────────────────────────────────────────────────────────────────────────────

  const draft = useMemo<PlanningDraft | null>(() => {
    // The open form wins: it is what the user is looking at, and the draft underneath is only where
    // things stood before it opened. Outside planning mode the live block is the only one there is —
    // that is the whole of the "edit an existing Termin and watch it move" case.
    const placed = liveDraft ?? (plan.isPlanning ? planning.draft : null);
    if (!placed) return null;

    const layout = draftLayout(placed, liveColors);
    const targetRow = rows.find((row) => row.technicianId === placed.technicianId);

    return {
      technicianId: placed.technicianId,
      layout,
      // Nothing to exclude for a fresh draft — it is not on the board yet. For an edit it is the
      // entry standing behind the live block, which must not be reported as its own conflict.
      colliding: targetRow
        ? detectDragCollision(
            draftCollisionBlocks(targetRow.blocks),
            layout,
            editedBlock?.key ?? '',
          ).length > 0
        : false,
      // Covers the initial pick too: a click in the grey zone lands a draft that is already flagged,
      // without a drag having happened.
      outsideShift: targetRow ? isOutsideShift(layout, targetRow.shift) : false,
      state: placed,
    };
  }, [
    liveDraft,
    plan.isPlanning,
    planning.draft,
    liveColors,
    editedBlock,
    rows,
    draftCollisionBlocks,
  ]);

  /** The picked slot, as the modals want it. `undefined` before anything is picked. */
  const plannedSlot = useMemo(
    () => (planning.draft ? draftToDefaultSlot(planning.draft, day.dayIso) : undefined),
    [planning.draft, day.dayIso],
  );

  /**
   * What each modal is handed: the live slot once it is open — that is how a drag reaches the form —
   * and the picked slot before that, which is what seeds the form when it opens.
   *
   * Gated on the modal being open (or its mode being active) so the other modal, which stays mounted
   * and closed, is never handed a slot belonging to the one in use.
   */
  const appointmentSlot =
    appointmentModal.isOpen || plan.mode === 'schedule-appointment'
      ? formSlot ?? plannedSlot ?? undefined
      : undefined;

  const taskSlot =
    taskModal.isOpen || plan.mode === 'schedule-task'
      ? formSlot ?? plannedSlot ?? undefined
      : undefined;

  const handleConfirmPlan = useCallback(() => {
    if (!planning.draft) return;

    if (plan.mode === 'schedule-appointment') {
      // An existing Termin is moved through the *edit* form, so the picked slot lands on the
      // appointment that is already there instead of creating a second one.
      if (plan.appointmentId != null) openEditAppointment(plan.appointmentId);
      else openCreateAppointment(plan.workOrderId ?? undefined);
      return;
    }
    // An existing task gets its first Termin through the *edit* form — `PATCH /tasks/:id` ignores
    // schedule fields on a task with no entry, so the modal follows it with `/tasks/:id/schedule`.
    if (plan.taskId != null) openEditTask(plan.taskId);
    else openCreateTask();
  }, [
    planning.draft,
    plan.mode,
    plan.workOrderId,
    plan.appointmentId,
    plan.taskId,
    openCreateAppointment,
    openEditAppointment,
    openCreateTask,
    openEditTask,
  ]);

  /**
   * Planning is done — drop out of the mode so the board goes back to normal.
   *
   * Guarded: the modals also fire `onSuccess` for an ordinary edit opened by clicking a block, and
   * that must not clear URL params it never set.
   */
  const handlePlanSaved = useCallback(() => {
    if (!plan.isPlanning) return;
    planning.clear();
    plan.exit();
  }, [plan, planning]);

  const pickedTechnicianName =
    rows.find((row) => row.technicianId === planning.draft?.technicianId)?.technicianName ?? null;

  /**
   * Bring the seeded draft into view, once per arrival.
   *
   * The board is 3104px wide across sixteen hours, so a Termin at 09:00 in the fifth Handwerker's
   * row can be off-screen on both axes — being sent to the right day is no use if you then have to
   * hunt for it. Rect maths against the container rather than `toXPx`, so the sticky technician
   * column and the lane stack need no re-derivation; not `scrollIntoView`, which would also scroll
   * ancestors and push the sticky header out of the viewport.
   *
   * `planning.draft` is in the deps and not decoration: the seed lands in `usePlanningDraft`'s state
   * during the same commit this effect first runs in, so on that pass the element does not exist
   * yet. Bailing without setting the ref lets the next render — the one that actually mounts the
   * draft — do the scroll.
   *
   * The ref key means this fires on arrival and on a jump back to the source day, and never fights
   * the dispatcher's own scrolling afterwards.
   */
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const didFocusRef = useRef<string | null>(null);

  useEffect(() => {
    if (!source || source.dayIso !== day.dayIso) return;

    const key = `${source.kind}:${source.ownerId}:${day.dayIso}`;
    if (didFocusRef.current === key) return;

    const container = scrollRef.current;
    const block = container?.querySelector<HTMLElement>('[data-planning-draft]');
    if (!container || !block) return;

    const containerBox = container.getBoundingClientRect();
    const blockBox = block.getBoundingClientRect();

    // Enough lead-in to show the slot in context rather than flush against the technician column.
    container.scrollLeft += blockBox.left - containerBox.left - TECHNICIAN_COL_WIDTH - 80;
    container.scrollTop += blockBox.top - containerBox.top - 80;

    didFocusRef.current = key;
  }, [source, day.dayIso, rows, planning.draft]);

  /**
   * Keep the block the form is editing out from under the docked panel.
   *
   * Keyed on the row and the day, not on the slot: scrolling on every keystroke would fight the
   * dispatcher's own scrolling while they type. Moving the block to another Handwerker or another
   * day is the one case where it can leave the screen entirely, and then it is brought back.
   */
  const liveFocusKey =
    modalOpen && liveDraft ? `${liveDraft.technicianId}:${day.dayIso}` : null;
  const didFocusLiveRef = useRef<string | null>(null);

  useEffect(() => {
    if (!liveFocusKey) {
      didFocusLiveRef.current = null;
      return;
    }
    if (didFocusLiveRef.current === liveFocusKey) return;

    const container = scrollRef.current;
    const block = container?.querySelector<HTMLElement>('[data-planning-draft]');
    // The live block mounts one render after the slot arrives; leaving the ref unset lets that
    // render do the scrolling. Same reasoning as the seed effect above.
    if (!container || !block) return;

    const containerBox = container.getBoundingClientRect();
    const blockBox = block.getBoundingClientRect();

    // The usable strip ends where the panel begins, and starts after the sticky technician column.
    const visibleRight = containerBox.right - DOCKED_FORM_WIDTH;
    const visibleLeft = containerBox.left + TECHNICIAN_COL_WIDTH;

    if (blockBox.right > visibleRight) container.scrollLeft += blockBox.right - visibleRight + 40;
    else if (blockBox.left < visibleLeft) container.scrollLeft += blockBox.left - visibleLeft - 40;

    if (blockBox.top < containerBox.top || blockBox.bottom > containerBox.bottom) {
      container.scrollTop += blockBox.top - containerBox.top - 80;
    }

    didFocusLiveRef.current = liveFocusKey;
  }, [liveFocusKey, rows]);

  /**
   * Land on the Handwerker the week view was pointing at.
   *
   * Arriving from a week cell you already know whose day you asked for; with a dozen rows on a
   * board that scrolls, the answer is often below the fold. Vertical only — the week says nothing
   * about a time, so moving the hour axis would be inventing an intent.
   *
   * The param is cleared once consumed, so it is a one-shot instruction and not a piece of state
   * that would re-scroll on every later render.
   */
  const [focusTechnician, setFocusTechnician] = useQueryState(
    FOCUS_TECHNICIAN_PARAM,
    parseAsInteger,
  );
  const didFocusTechnicianRef = useRef<number | null>(null);

  useEffect(() => {
    if (focusTechnician == null) {
      didFocusTechnicianRef.current = null;
      return;
    }
    if (didFocusTechnicianRef.current === focusTechnician) return;

    const container = scrollRef.current;
    // The rows mount a render after the query resolves; bailing without setting the ref lets that
    // render do the scrolling. Same reasoning as the two effects above.
    const track = container?.querySelector<HTMLElement>(
      `[${ROW_TECHNICIAN_ATTR}="${focusTechnician}"]`,
    );
    if (!container || !track) return;

    const containerBox = container.getBoundingClientRect();
    const trackBox = track.getBoundingClientRect();
    // Clear of the sticky hour axis rather than flush under it.
    container.scrollTop += trackBox.top - containerBox.top - 80;

    didFocusTechnicianRef.current = focusTechnician;
    void setFocusTechnician(null);
  }, [focusTechnician, rows, setFocusTechnician]);

  return (
    <div className="flex h-full flex-col">
      <div ref={scrollRef} className="relative min-h-0 flex-1 overflow-auto bg-white">
        {/*
          Widened, not padded, while the form is docked: `padding-right` on a scroll container is not
          reliably part of the scrollable area, and without the extra width the last hours of the day
          could never be scrolled out from under the panel.
        */}
        <div
          style={{
            width: TECHNICIAN_COL_WIDTH + BOARD_WIDTH + (modalOpen ? DOCKED_FORM_WIDTH : 0),
          }}
        >
          <div
            className={`sticky top-0 flex border-b border-slate-200 bg-white ${BOARD_LAYER.header}`}
          >
            <div
              className={`sticky left-0 shrink-0 bg-white px-3 py-3 text-base text-zinc-500 outline outline-1 outline-offset-[-0.50px] outline-slate-200 ${BOARD_LAYER.headerCorner}`}
              style={{ width: TECHNICIAN_COL_WIDTH }}
            >
              Handwerkern
            </div>
            <BoardTimeAxis />
          </div>

          {isLoading ? (
            <BoardSkeleton />
          ) : isError ? (
            <BoardMessage
              text="Der Kalender konnte nicht geladen werden."
              action={{ label: 'Erneut versuchen', onClick: () => refetch() }}
            />
          ) : rows.length === 0 ? (
            <BoardMessage text="Keine Handwerker gefunden." />
          ) : (
            <div className="relative">
              {rows.map((row) => (
                <BoardRow
                  key={row.technicianId}
                  row={row}
                  dayIso={day.dayIso}
                  dragContext={dragContext}
                  preview={preview}
                  onDragStart={onDragStart}
                  wasDragged={wasDragged}
                  // A click on the track places a slot — but not while a form is open on top of it,
                  // where a click meant to bring the board into focus would teleport the block.
                  pickable={plan.isPlanning && !modalOpen}
                  blocksInert={plan.isPlanning || modalOpen}
                  dimmedKey={editedBlock?.key ?? null}
                  // Hover cards stay away for the length of a gesture, and stay clear of the docked
                  // panel — they sit below it in the stacking order, so they move rather than lose.
                  dragActive={drag !== null}
                  reservedRightPx={modalOpen ? DOCKED_FORM_WIDTH : 0}
                  draft={draft}
                  onPickSlot={planning.place}
                  onDraftDragStart={onDraftDragStart}
                />
              ))}
              {day.isToday && <BoardCurrentTimeLine offsetX={TECHNICIAN_COL_WIDTH} />}
            </div>
          )}
        </div>
      </div>

      {plan.isPlanning && (
        <PlanningBar
          plan={plan}
          dayIso={day.dayIso}
          source={source}
          onGoToSourceDay={source ? () => day.goToDay(source.dayIso) : undefined}
          draft={planning.draft}
          pickedTechnicianName={pickedTechnicianName}
          colliding={draft?.colliding ?? false}
          outsideShift={draft?.outsideShift ?? false}
          onConfirm={handleConfirmPlan}
        />
      )}

      {/*
        Siblings of the scroll container, not children of it — a modal nested inside
        `overflow-auto` would be clipped and would scroll with the grid.

        Mounted once for the whole board: every block only writes the URL and the modals read it,
        which is also what makes
        `/disposition?day=2026-08-11&upsert-appointment=edit&upsert-appointment-id=42` a working
        deep link.

        `placement="side"` docks them beside the board instead of over it, which is what makes the
        live preview worth having — the block and the fields that describe it are on screen together.
        `defaultSlot` and `onSlotChange` are the two halves of that loop; `formSlot` above owns the
        value both of them read and write.
      */}
      <UpsertAppointmentModal
        defaultSlot={appointmentSlot}
        onSlotChange={handleSlotChange}
        onFullDayChange={setFormIsFullDay}
        placement="side"
        onSuccess={handlePlanSaved}
      />
      <UpsertTaskModal
        defaultSlot={taskSlot}
        onSlotChange={handleSlotChange}
        placement="side"
        onSuccess={handlePlanSaved}
      />

      {/*
        Last in the DOM on purpose: every modal shares z-index 100, so a dialog raised by a drag has
        to come after the two upsert modals to sit above one that happens to be open.

        The wording says "verschieben" rather than the modals' "speichern" — this path is a drag,
        and the amber ghost has already said the same thing before the drop.
      */}
      <ScheduleConflictDialogs
        flow={conflictFlow}
        isLoading={updateEntry.isPending || upsertBreak.isPending}
        onGoToReschedule={() => navigate(routes.reschedule.root)}
        conflictTitle="Terminüberschneidung erkannt"
        conflictDescription="An dieser Zeit liegt bereits ein Eintrag. Möchten Sie den Block trotzdem verschieben?"
        conflictConfirmLabel="Trotzdem verschieben"
        workingHoursDescription="Der Techniker arbeitet zu dieser Zeit nicht. Möchten Sie den Block trotzdem verschieben?"
        blockersDescription="Die folgenden abgeschlossenen Einträge blockieren diesen Zeitraum."
      />
    </div>
  );
};
